import type { SetupContext } from 'vue';
import type { HekateDisplayField, HekateDisplayForm } from '@onshape/www-inertia';
import { Trans } from '@onshape/www-inertia';
import ServiceEndpointsUtil from '../../../util/ServiceEndpointsUtil';
import AntiSpamUtil from '../../../util/AntiSpamUtil';
import ExternalUtil from '../../../ExternalUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import FormFieldCoordinator from './FormFieldCoordinator';

export default class AutoCompleteFieldCoordinator extends FormFieldCoordinator {
  public showElement: Ref<boolean>;

  public query: Ref<string>;

  public filteredOptions: Ref<string[]>;

  public activeIndex: Ref<number>;

  public options: Ref<string[]>;

  constructor({
    context,
    field,
    modelValue,
    formDefinition,
  }: {
    field: Ref<HekateDisplayField>;
    context: SetupContext;
    modelValue: Ref<string>;
    formDefinition: Ref<HekateDisplayForm>;
  }) {
    super({
      context,
      field,
      modelValue,
      formDefinition,
    });

    this.showElement = ref(false);
    this.query = ref('');
    this.filteredOptions = ref([]);
    this.activeIndex = ref(-1);
    this.options = ref([]);
  }

  public static async getOptions(htmlName: string): Promise<string[]> {
    try {
      const resp = await fetch(`${ServiceEndpointsUtil.autoCompleteEndpoint}?list=${htmlName}`, {
        method: 'GET',
        headers: {
          ...(await AntiSpamUtil.commonFetchHeaders()),
        },
      });
      const options = await resp.json();
      return options.elements.length > 0 ? options.elements : [];
    } catch {
      ErrorUtil.safePostMessageToSentry({
        tag: 'HekateLoadError',
        msg: `Failed to load autoComplete Options for ${htmlName}`,
      });
      return [];
    }
  }

  setup() {
    onMounted(async () => {
      const resp = await AutoCompleteFieldCoordinator.getOptions(this.field.value.htmlName);
      this.options.value = resp;

      document.addEventListener('click', (event) => this.handleClickOutside(event));
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', (event) => this.handleClickOutside(event));
    });

    const { $staticImages } = useNuxtApp();
    const searchImageSrc = $staticImages['search'];
    const closeImageSrc = $staticImages['close'];

    return {
      bubbleModel: this.buildBubbleModel(),
      selectOption: this.selectOption.bind(this),
      onArrowDown: this.onArrowDown.bind(this),
      onArrowUp: this.onArrowUp.bind(this),
      onEnter: this.onEnter.bind(this),
      highlightedOption: this.highlightedOption.bind(this),
      clearQuery: this.clearQuery.bind(this),
      filterOptions: this.filterOptions.bind(this),
      onFocus: this.onFocus.bind(this),
      onBlur: this.onBlur.bind(this),
      showElement: this.showElement,
      query: this.query,
      filteredOptions: this.filteredOptions,
      activeIndex: this.activeIndex,
      inputType: 'text',
      placeholder: Trans.by('Start typing to search...', 'autoCompletePlaceholder'),
      suggested: Trans.by('Suggested', 'autoCompleteSuggested'),
      noSuggestions: Trans.by('NO SUGGESTIONS', 'autoCompleteNoSuggestions'),
      startTyping: Trans.by('START TYPING TO SEARCH', 'autoCompleteStartTyping'),
      searchImageSrc,
      closeImageSrc,
    };
  }

  public filterOptions() {
    this.showElement.value = true;
    this.field.value.val = this.query.value;
    this.filteredOptions.value = this.options.value
      .filter((option: string) => option.toLowerCase().startsWith(this.query.value.toLowerCase()))
      .slice(0, 5);
  }

  public clearQuery() {
    this.query.value = '';
    this.showElement.value = false;
    this.filteredOptions.value = this.options.value;
    this.activeIndex.value = -1;
    this.field.value.val = '';
    this.onBlur();
  }

  public selectOption(option: string, index: number) {
    this.query.value = option;
    this.modelValue.value = option;
    this.filteredOptions.value = [];
    this.showElement.value = false;
    this.activeIndex.value = index;
    this.field.value.val = option;
  }

  public onArrowDown() {
    if (this.activeIndex.value < this.filteredOptions.value.length - 1) {
      this.activeIndex.value += 1;
    }
  }

  public onArrowUp() {
    if (this.activeIndex.value > 0) {
      this.activeIndex.value -= 1;
    }
  }

  public onEnter() {
    if (this.activeIndex.value >= 0) {
      this.selectOption(
        this.filteredOptions.value[this.activeIndex.value] || '',
        this.activeIndex.value
      );
    }
  }

  public onFocus() {
    this.field.value.interactiveState = 'dirty';
    this.field.value.errorMessages = [];
    this.showElement.value = true;
    this.context.emit('focus', this.field.value);
  }

  public onBlur() {
    this.showElement.value = false;
    this.context.emit('blur', this.field.value);
  }

  public handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (this.field.value.interactiveState !== 'dirty') return;

    if (target.closest('.auto-complete-wrapper')) return;

    this.onBlur();
  }

  public highlightedOption(option: string) {
    const lowerOption = option.toLowerCase();
    const lowerQuery = this.query.value.toLowerCase();

    if (!lowerOption.startsWith(lowerQuery)) {
      return option; // No match, return as is.
    }

    const saneText = ExternalUtil.sanitizeHtml(this.query.value);
    // Return a string where the shared part is bold
    const restOfString = option.slice(this.query.value.length);

    return [saneText, restOfString];
  }
}
